import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayerStats } from '../data/types';
import { ArrowUpDown, Bird, Star, Trophy, Minus, XCircle, Search } from 'lucide-react';

interface PlayerStatsTableProps {
  stats: PlayerStats[];
  teamFilter?: string;
  onTeamFilterChange?: (teamId: string) => void;
  teams?: { id: string; name: string; color: string }[];
}

type SortKey = 'playerName' | 'eagles' | 'birdies' | 'holesWon' | 'holesHalved' | 'holesLost' | 'winRate' | 'totalHolesPlayed' | 'gamesPlayed';

const thIcon: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.2rem',
};

export function PlayerStatsTable({ stats, teamFilter, onTeamFilterChange, teams }: PlayerStatsTableProps) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('winRate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [nameSearch, setNameSearch] = useState('');

  const filteredStats = stats
    .filter(s => !teamFilter || s.teamId === teamFilter)
    .filter(s => !nameSearch || s.playerName.toLowerCase().includes(nameSearch.toLowerCase()));

  const sortedStats = [...filteredStats].sort((a, b) => {
    let av: number | string = a[sortKey];
    let bv: number | string = b[sortKey];
    if (typeof av === 'string') {
      av = av.toLowerCase();
      bv = (bv as string).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
    }
    return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'playerName' ? 'asc' : 'desc');
    }
  }

  function thClass(key: SortKey) {
    return sortKey === key ? 'sorted' : '';
  }

  return (
    <div>
      <div className="flex items-center gap-sm mb-md" style={{ flexWrap: 'nowrap' }}>
        <div style={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
          <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            className="input"
            placeholder="Vyhledat hráče..."
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            style={{ paddingLeft: '2.25rem', width: '100%' }}
          />
        </div>
        {teams && teams.length > 0 && (
          <select
            className="input"
            style={{ width: 'auto', minWidth: '120px', flex: '0 0 auto' }}
            value={teamFilter ?? ''}
            onChange={(e) => onTeamFilterChange?.(e.target.value)}
          >
            <option value="">Všechny týmy</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="stats-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('playerName')} className={`player-name ${thClass('playerName')}`}>
                <span style={thIcon}>Hráč <ArrowUpDown size={12} /></span>
              </th>
              <th>Tým</th>
              <th onClick={() => handleSort('gamesPlayed')} className={`${thClass('gamesPlayed')}`} style={{ textAlign: 'right' }}>
                Hry
              </th>
              <th onClick={() => handleSort('totalHolesPlayed')} className={`${thClass('totalHolesPlayed')}`} style={{ textAlign: 'right' }}>
                Jamky
              </th>
              <th onClick={() => handleSort('eagles')} className={`${thClass('eagles')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  <Star size={12} color="#f59e0b" /> Eagle
                </span>
              </th>
              <th onClick={() => handleSort('birdies')} className={`${thClass('birdies')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  <Bird size={12} color="var(--success)" /> Birdie
                </span>
              </th>
              <th onClick={() => handleSort('holesWon')} className={`${thClass('holesWon')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  <Trophy size={12} color="var(--success)" /> Won
                </span>
              </th>
              <th onClick={() => handleSort('holesHalved')} className={`${thClass('holesHalved')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  <Minus size={12} /> Halved
                </span>
              </th>
              <th onClick={() => handleSort('holesLost')} className={`${thClass('holesLost')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  <XCircle size={12} color="var(--error)" /> Lost
                </span>
              </th>
              <th onClick={() => handleSort('winRate')} className={`${thClass('winRate')}`} style={{ textAlign: 'right' }}>
                <span style={{ ...thIcon, justifyContent: 'flex-end' }}>
                  Win % <ArrowUpDown size={12} />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedStats.map(s => (
              <tr key={s.playerId} onClick={() => navigate(`/stats/${s.playerId}`)} style={{ cursor: 'pointer' }}>
                <td className="player-name">
                  <span className="team-badge" style={{ backgroundColor: s.teamColor }} />
                  {s.playerName}
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                  {s.teamName}
                </td>
                <td className="numeric">{s.gamesPlayed}</td>
                <td className="numeric">{s.totalHolesPlayed}</td>
                <td className="numeric" style={{ color: s.eagles > 0 ? '#f59e0b' : undefined, fontWeight: s.eagles > 0 ? 700 : undefined }}>
                  {s.eagles}
                </td>
                <td className="numeric" style={{ color: s.birdies > 0 ? 'var(--success)' : undefined }}>
                  {s.birdies}
                </td>
                <td className="numeric" style={{ color: s.holesWon > 0 ? 'var(--success)' : undefined }}>
                  {s.holesWon}
                </td>
                <td className="numeric">{s.holesHalved}</td>
                <td className="numeric" style={{ color: s.holesLost > 0 ? 'var(--error)' : undefined }}>
                  {s.holesLost}
                </td>
                <td className="numeric" style={{ fontWeight: 700, color: 'var(--accent)' }}>
                  {s.winRate}%
                </td>
              </tr>
            ))}
            {sortedStats.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                  Zatím žádné statistiky
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
